import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash.debounce';
import DataTable from 'react-data-table-component';
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from 'reactstrap';
import classnames from 'classnames';
import ReferralCodeManagementTab from '../Components/ReferralCodeManagementTab';
import Breadcrumbs from '../CommonElements/Breadcrumbs';
import {
  createDiscountCoupon,
  getDiscountCoupons,
} from '../ReduxToolkit/Reducers/organizationManagement';

function DiscountCoupons() {
  const LOAD_MORE_OPTION_VALUE = '__load_more__';
  const ORGANIZATION_PAGE_SIZE = 20;

  const dispatch = useDispatch();
  const { discountCoupons, totalDiscountCoupons, isLoading } = useSelector(
    (state) => state.organizationManagement,
  );

  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchText, setSearchText] = useState('');
  const [tableData, setTableData] = useState([]);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('1');

  const [form, setForm] = useState({
    name: '',
    code: '',
    apply_type: 'one_time',
    value_type: 'percentage',
    percent_off: '',
    amount_off: '',
    currency: 'eur',
    redeem_by: '',
  });

  useEffect(() => {
    if (page) {
      fetchCoupons();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, itemsPerPage, searchText]);

  useEffect(() => {
    if (discountCoupons[page]) {
      setTableData(discountCoupons[page]);
    }
  }, [discountCoupons, page]);



  const fetchCoupons = async (hideLoader = false) => {
    await dispatch(getDiscountCoupons(page, searchText, itemsPerPage, hideLoader));
  };



  const searchCoupons = (inputValue) => {
    const query = inputValue?.trim();
    setPage(1);
    setSearchText(query);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceSearch = useCallback(debounce(searchCoupons, 700), []);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    debounceSearch(e.target.value);
  };

  const onCreateCoupon = async () => {
    if (!form.name.trim()) return;

    const payload = {
      name: form.name.trim(),
      code: form.code.trim(),
      apply_type: form.apply_type,
      value_type: form.value_type,
      percent_off:
        form.value_type === 'percentage' ? Number(form.percent_off) : undefined,
      amount_off:
        form.value_type === 'fixed_amount' ? Number(form.amount_off) : undefined,
      currency:
        form.value_type === 'fixed_amount' ? form.currency.toLowerCase() : undefined,
      redeem_by: form.redeem_by || undefined,
    };

    const result = await dispatch(createDiscountCoupon(payload));
    if (result?.statusCode === 200) {
      setCreateModalOpen(false);
      setForm({
        name: '',
        code: '',
        apply_type: 'one_time',
        value_type: 'percentage',
        percent_off: '',
        amount_off: '',
        currency: 'eur',
        redeem_by: '',
      });
      fetchCoupons();
    }
  };



  const columns = [
    {
      name: 'Coupon Name',
      selector: (row) => row.name,
      sortable: true,
      minWidth: '160px',
    },
    {
      name: 'Coupon Code',
      selector: (row) => row.code,
      sortable: true,
      minWidth: '160px',
    },
    {
      name: 'Discount',
      selector: (row) => row.discount_label || '--',
      minWidth: '120px',
    },
    {
      name: 'Type',
      cell: (row) =>
        row.apply_type === 'one_time' ? 'One Time Use' : 'Whole Subscription',
      minWidth: '170px',
    },

  ];

  return (
    <div className="page-body">
      <Breadcrumbs
        pageTitle="Discount Coupons"
        parent="Home"
        title="Discount Coupons"
      />
      <Container fluid>
        <Nav tabs className="mb-3">
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === '1' })}
              onClick={() => setActiveTab('1')}
              style={{ cursor: 'pointer' }}
            >
              Discount Coupons
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === '2' })}
              onClick={() => setActiveTab('2')}
              style={{ cursor: 'pointer' }}
            >
              Referral Codes
            </NavLink>
          </NavItem>
        </Nav>
        
        <TabContent activeTab={activeTab}>
          <TabPane tabId="1">
            <Card>
              <CardBody>
            <Row className="align-items-center justify-content-between mb-3">
              <Col xs="auto">
                <Button color="primary" onClick={() => setCreateModalOpen(true)}>
                  Create Coupon
                </Button>
              </Col>
              <Col xs="auto" className="d-flex align-items-center gap-2">
                <Label className="mb-0">Search:</Label>
                <Input
                  value={searchTerm}
                  onChange={handleSearchChange}
                  placeholder="Search by name or code"
                  style={{ minWidth: 320 }}
                />
              </Col>
            </Row>

            <DataTable
              striped
              pagination
              paginationServer
              data={tableData}
              columns={columns}
              progressPending={isLoading}
              paginationTotalRows={totalDiscountCoupons}
              paginationPerPage={itemsPerPage}
              paginationDefaultPage={page}
              customStyles={{ tableWrapper: { style: { minHeight: 500 } } }}
              onChangePage={(newPage) => setPage(newPage)}
              onChangeRowsPerPage={(count) => {
                setPage(1);
                setItemsPerPage(count);
              }}
            />
          </CardBody>
        </Card>
          </TabPane>
          <TabPane tabId="2">
            <ReferralCodeManagementTab />
          </TabPane>
        </TabContent>
      </Container>

      <Modal isOpen={createModalOpen} toggle={() => setCreateModalOpen(false)} size="lg">
        <ModalHeader toggle={() => setCreateModalOpen(false)}>
          Create Discount Coupon
        </ModalHeader>
        <ModalBody>
          <Row className="g-3">
            <Col md={6}>
              <FormGroup>
                <Label>Coupon Name</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="Summer Offer"
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Coupon Code</Label>
                <Input
                  value={form.code}
                  onChange={(e) => setForm((prev) => ({ ...prev, code: e.target.value }))}
                  placeholder="SUMMER25"
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Usage Type</Label>
                <Input
                  type="select"
                  value={form.apply_type}
                  onChange={(e) => setForm((prev) => ({ ...prev, apply_type: e.target.value }))}
                >
                  <option value="one_time">One Time Use</option>
                  <option value="whole_subscription">Whole Subscription</option>
                </Input>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Discount Type</Label>
                <Input
                  type="select"
                  value={form.value_type}
                  onChange={(e) => setForm((prev) => ({ ...prev, value_type: e.target.value }))}
                >
                  <option value="percentage">Percentage</option>
                  <option value="fixed_amount">Fixed Amount</option>
                </Input>
              </FormGroup>
            </Col>
            {form.value_type === 'percentage' ? (
              <Col md={6}>
                <FormGroup>
                  <Label>Discount (%)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="100"
                    value={form.percent_off}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, percent_off: e.target.value }))
                    }
                    placeholder="20"
                  />
                </FormGroup>
              </Col>
            ) : (
              <>
                <Col md={6}>
                  <FormGroup>
                    <Label>Amount</Label>
                    <Input
                      type="number"
                      min="1"
                      value={form.amount_off}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, amount_off: e.target.value }))
                      }
                      placeholder="50"
                    />
                  </FormGroup>
                </Col>
                {/* <Col md={6}>
                  <FormGroup>
                    <Label>Currency</Label>
                    <Input
                      value={form.currency}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, currency: e.target.value }))
                      }
                      placeholder="eur"
                    />
                  </FormGroup>
                </Col> */}
              </>
            )}
            {/* <Col md={6}>
              <FormGroup>
                <Label>Redeem By (Optional)</Label>
                <Input
                  type="datetime-local"
                  value={form.redeem_by}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, redeem_by: e.target.value }))
                  }
                />
              </FormGroup>
            </Col> */}
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setCreateModalOpen(false)}>
            Cancel
          </Button>
          <Button color="primary" onClick={onCreateCoupon}>
            Create Coupon
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

export default DiscountCoupons;
